import { useEffect, useRef, useState } from "react";
import PrimaryButton from "../../_utils/CommonButtons/PrimaryButton";
import SVGComponent from "../../_utils/SVGComponent";
import ICONS from "../../../assets/Icons";
import IconRoundButton from "../../_utils/CommonButtons/IconRoundButton";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../utils/configurationProvider";
import { linkIdentifier } from "../../../utils/linkIdentifier";
import CardActionableButton from "../../_utils/CommonViewAllCard/CardSubComponent/CardActionableButton";

const ChannelCreationGuidePopUp = ({
  logoUrl,
  copyLogoURL,
  setCopyLogoURL,
  channelPayLoad,
  channelCreationGuide,
  setChannelCreationGuide,
}) => {
  const [animate, setAnimate] = useState(false);
  const [stepIndex, setStepIndex] = useState(
    GlobalConfiguration?.lengthCheckZero,
  );
  const [copiedNotificationId, setCopiedNotificationId] = useState(false);
  const [direction, setDirection] = useState("next");
  const [animKey, setAnimKey] = useState(GlobalConfiguration?.lengthCheckZero);

  const channelNameMap = {
    gchat: "Google Chat",
    teams: "Microsoft Teams",
    slack: "Slack",
  };

  const referenceChannelName = channelNameMap[channelPayLoad?.channelTarget];

  const gchatSteps = [
    {
      title: "1.1 Create a Space",
      instructions: [
        <>
          Open Gmail and go to <span className="font-semibold">Chat</span>.
        </>,
        <>
          Click <span className="font-semibold">New chat</span> and select{" "}
          <span className="font-semibold">Create a space</span>.
        </>,
      ],
      icon: ICONS?.GOOGLE_CHAT1,
      alt: "GOOGLE1",
    },
    {
      title: "1.2 Create a Space",
      instructions: [
        <>
          Enter a <span className="font-semibold">name</span> for the space (for
          example, “Notifications”).
        </>,
        <>
          Click <span className="font-semibold">Create</span>.
        </>,
      ],
      icon: ICONS?.GOOGLE_CHAT2,
      alt: "GOOGLE2",
    },
    {
      title: "2.1 Generate the Webhook URL",
      instructions: [
        "Open the space you created.",
        "Click on the Space name at the top of the chat window to open the dropdown menu.",
        <>
          Select <span className="font-semibold">Apps & integrations</span>.
        </>,
      ],
      icon: ICONS?.GOOGLE_CHAT3,
      alt: "GOOGLE3",
    },
    {
      title: "2.2 Generate the Webhook URL",
      instructions: [
        <>
          Select <span className="font-semibold">Add webhooks</span>.
        </>,
      ],
      icon: ICONS?.GOOGLE_CHAT4,
      alt: "GOOGLE4",
    },
    {
      title: "2.3 Generate the Webhook URL",
      instructions: [
        `Enter a name for this webhook (like "Notifications") and hit Save.`,
        "Paste this avatar URL in the Google Chat webhook's avatar URL field to display the Xpentra avatar for notifications.",
      ],
      showLogoUrl: true,
      icon: ICONS?.GOOGLE_CHAT5,
      alt: "GOOGLE5",
    },
    {
      title: "2.4 Generate the Webhook URL",
      instructions: [
        `Copy the provided URL. This is the endpoint URL you will need to send your webhooks to.`,
      ],
      icon: ICONS?.GOOGLE_CHAT6,
      alt: "GOOGLE6",
    },
  ];

  const teamsSteps = [
    {
      title: "1. Open Connectors",
      instructions: [
        "Navigate to the channel you want to send messages to and click on ••• in the top nav bar.",
        <>
          Click <span className="font-semibold">Connectors</span> in the
          dropdown menu.
        </>,
      ],
      icon: ICONS?.MICROSOFT1,
      alt: "MICRO1",
    },
    {
      title: "2. Configure Webhook",
      instructions: [
        <>
          Type <span className="font-semibold">incoming webhook</span> in the
          search bar and click <span className="font-semibold">Add</span>.
        </>,
        <>
          Click <span className="font-semibold">Configure</span> and name your
          webhook. We suggest naming it to indicate the source of the webhook.
        </>,
      ],
      icon: ICONS?.MICROSOFT2,
      alt: "MICRO2",
    },
    {
      title: "3. Copy Webhook URL",
      instructions: [
        "Copy the provided URL. This is the endpoint URL you will need to send your webhooks to.",
      ],
      icon: ICONS?.MICROSOFT3,
      alt: "MICRO3",
    },
  ];

  const slackSteps = [
    {
      title: "1. Create a Slack App",
      instructions: [
        "Visit the Slack API website.",
        <>
          Click <span className="font-semibold">Create an App</span>.
        </>,
      ],
      icon: ICONS?.SLACK1,
      alt: "SLACK1",
    },
    {
      title: "2. Select App Setup Type",
      instructions: [
        <>
          Choose <span className="font-semibold">Blank app</span> under the{" "}
          <span className="font-semibold">Or start your own way</span> section.
        </>,
        <>
          Click <span className="font-semibold">Continue</span>.
        </>,
      ],
      icon: ICONS?.SLACK2,
      alt: "SLACK2",
    },
    {
      title: "3. Configure App Details",
      instructions: ["Give your app a name and select your workspace."],
      icon: ICONS?.SLACK3,
      alt: "SLACK3",
    },

    {
      title: "4. Enable Incoming Webhooks",
      instructions: [
        <>
          In the features sidebar on the left, find and click the{" "}
          <span className="font-semibold">Incoming Webhooks</span> section.
        </>,
        <>
          Toggle the feature to <span className="font-semibold">On</span> to
          activate it.
        </>,
      ],
      icon: ICONS?.SLACK4,
      alt: "SLACK4",
    },
    {
      title: "5. Request Webhook Authorization",
      instructions: [
        <>
          Scroll down to the{" "}
          <span className="font-semibold">Webhook URLs for Your Workspace</span>{" "}
          section.
        </>,
        <>
          Click <span className="font-semibold">Add New Webhook</span>.
        </>,
      ],
      icon: ICONS?.SLACK5,
      alt: "SLACK5",
    },
    {
      title: "6. Authorize Workspace and Channel",
      instructions: [
        "Select your workspace and the specific channel for the webhook to post messages to.",
        <>
          Click <span className="font-semibold">Allow</span> to authorize the
          app.
        </>,
      ],
      icon: ICONS?.SLACK6,
      alt: "SLACK6",
    },
    {
      title: "7. Retrieve Webhook URL",
      instructions: [
        "You will be redirected back to the app settings page.",
        <>
          Your new webhook URL will now be displayed under Webhook URLs for Your
          Workspace. Click <span className="font-semibold">Copy</span> to save
          it.
        </>,
      ],
      icon: ICONS?.SLACK7,
      alt: "SLACK7",
    },
  ];

  useEffect(() => {
    setStepIndex(GlobalConfiguration?.lengthCheckZero);
    setAnimKey(GlobalConfiguration?.lengthCheckZero);
  }, [channelPayLoad?.channelTarget]);

  const getActiveSteps = () => {
    switch (channelPayLoad?.channelTarget) {
      case "gchat":
        return gchatSteps;
      case "teams":
        return teamsSteps;
      case "slack":
        return slackSteps;
      default:
        return [];
    }
  };

  const steps = getActiveSteps();
  const currentStep = steps[stepIndex];

  const handleNext = () => {
    if (stepIndex < steps.length - GlobalConfiguration?.lengthCheckOne) {
      setDirection("next");
      setStepIndex((prev) => prev + GlobalConfiguration?.lengthCheckOne);
      setAnimKey((prev) => prev + GlobalConfiguration?.lengthCheckOne);
    }
  };

  const handleBack = () => {
    if (stepIndex > GlobalConfiguration?.lengthCheckZero) {
      setDirection("back");
      setStepIndex((prev) => prev - GlobalConfiguration?.lengthCheckOne);
      setAnimKey((prev) => prev + GlobalConfiguration?.lengthCheckOne);
    }
  };

  const handleClose = () => {
    setAnimate(true);
    setTimeout(() => {
      setChannelCreationGuide(false);
    }, IntervalConfiguration.intervalSemiMid);
  };

  if (!currentStep) return null;

  return (
    <div
      className="flex w-full justify-center items-center m-auto top-top-half left-left-half fixed z-z-index-modal"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`w-standard-largePopup-width fixed Qsmd:w-small-popup-width max-h-notification-popup-max-height h-alert-popup-height-1 xxl:max-h-alert-popup-height-2 tall:max-h-alert-popup-height-2 
                     overflow-hidden flex flex-col gap-y-gap-md py-padding-md mx-margin-md Qsmd:mx-margin-base bg-primary-bg rounded-border-radius-md animate-none
                      ${animate ? "Qsmd:animate-slideInFromLeft" : "Qsmd:animate-slideInFromRight"} overflow-y-hidden overflow-x-hidden`}
      >
        <div className="flex justify-between px-padding-md">
          <div className="flex flex-col gap-gap-xs ">
            <p className="text-text-primary text-font font-semibold">
              Guide for channel creation
            </p>
            <p className="text-font-xs text-text-secondary font-normal">
              Follow the steps below to create the channel.
            </p>
          </div>

          <IconRoundButton
            enabledBgColor={"var(--primary-nav)"}
            isLoading={false}
            handleFunction={() => {
              setAnimate(true);
              setTimeout(() => {
                handleClose();
              }, IntervalConfiguration.intervalSemiMid);
            }}
            title="Close"
            icon={"https://q2pay.in/new_assets/common/create_flow/close.svg"}
            hoverOption="white"
            btnType={"button"}
            isDisabled={false}
          />
        </div>

        <div className="flex flex-col flex-1 overflow-hidden gap-gap-md min-h-height-base">
          <p className="text-text-primary text-font-sm font-normal px-padding-md">
            {currentStep.title}
          </p>

          <div
            key={animKey}
            className={`flex flex-col flex-1 overflow-hidden gap-gap-md ${
              animKey === GlobalConfiguration?.lengthCheckZero
                ? ""
                : direction === "next"
                  ? "animate-slideInFromRightChannelPopUp"
                  : "animate-slideInFromLeftChannelPopUp"
            }`}
          >
            <div className="flex flex-col gap-gap-sm overflow-y-auto flex-1 px-padding-md">
              <ul className="flex flex-col list-disc list-outside ml-margin-md gap-gap-sm">
                {currentStep.instructions.map((text, i) => (
                  <li
                    key={i}
                    className="text-text-secondary text-font-xs font-normal"
                  >
                    {text}
                  </li>
                ))}
              </ul>

              {currentStep.showLogoUrl && (
                <div className="flex justify-between items-start ml-margin-md">
                  <p className="text-text-primary text-font-xs font-normal break-all">
                    {linkIdentifier(logoUrl)}
                  </p>

                  <CardActionableButton
                    handleFunction={async () => {
                      await navigator.clipboard.writeText(logoUrl);
                      setCopiedNotificationId(true);
                      setCopyLogoURL("Copied");
                      setTimeout(() => {
                        setCopiedNotificationId(false);
                        setCopyLogoURL("Copy to clipboard");
                      }, IntervalConfiguration.intervalMedium);
                    }}
                    icon={
                      copiedNotificationId
                        ? ICONS.SELECTED_WITH_GREEN_CIRCLE
                        : ICONS.COPY
                    }
                    title={copyLogoURL}
                    bgColor="var(--primary-nav)"
                  />
                </div>
              )}

              {currentStep.icon && (
                <SVGComponent
                  url={currentStep.icon}
                  height={"var(--height-auto)"}
                  alt={currentStep.alt}
                />
              )}
            </div>
          </div>
        </div>

        <div className="gap-gap-md flex justify-end px-padding-md">
          <PrimaryButton
            btnText={"Back"}
            btnType={"button"}
            handleFunction={handleBack}
            isDisabled={stepIndex === GlobalConfiguration?.lengthCheckZero}
            enabledTextColor={"var(--text-primary)"}
            enabledBgColor={"transparent"}
            outLineColor={"var(--text-primary)"}
            hoverOption={"white"}
            isLoading={false}
            isLoaderNeeded={false}
            fullWidthRequired={false}
          />
          <PrimaryButton
            btnText={
              stepIndex === steps.length - GlobalConfiguration?.lengthCheckOne
                ? "Done"
                : "Next"
            }
            btnType={"button"}
            handleFunction={
              stepIndex === steps.length - GlobalConfiguration?.lengthCheckOne
                ? () => {
                    setAnimate(true);
                    setTimeout(() => {
                      handleClose();
                    }, IntervalConfiguration.intervalSemiMid);
                  }
                : handleNext
            }
            isDisabled={steps.length === GlobalConfiguration?.lengthCheckZero}
            enabledTextColor={"var(--primary-bg)"}
            enabledBgColor={"var(--text-primary)"}
            hoverOption={"black"}
            isLoading={false}
            inActiveVariantNeeded={true}
            isLoaderNeeded={false}
            fullWidthRequired={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ChannelCreationGuidePopUp;
